import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { FolderOpen, Users, X, Loader2 } from 'lucide-react';

export function PendingInviteModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]); // {id, kind, name, inviter}
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [proj, team] = await Promise.all([api.get('/invites'), api.get('/team-invites')]);
      const list = [
        ...(proj.data || []).map(i => ({ id: i.id, kind: 'project', name: i.project_name, inviter: i.inviter_name })),
        ...(team.data || []).map(i => ({ id: i.id, kind: 'team', name: i.team_name, inviter: i.inviter_name })),
      ];
      setInvites(list);
      if (list.length && sessionStorage.getItem('pending_invites_seen') !== '1') setOpen(true);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const dismiss = () => { sessionStorage.setItem('pending_invites_seen', '1'); setOpen(false); };

  const accept = async (inv) => {
    setBusy(inv.id);
    try {
      await api.post(`/${inv.kind === 'project' ? 'invites' : 'team-invites'}/${inv.id}/accept`);
      toast.success(`Joined ${inv.name}`);
      const rest = invites.filter(i => i.id !== inv.id);
      setInvites(rest);
      window.dispatchEvent(new Event('workspace-changed'));
      window.dispatchEvent(new Event('mentions-updated'));
      if (inv.kind === 'team') navigate('/dashboard');
      if (!rest.length) setOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not accept'); }
    finally { setBusy(null); }
  };

  const decline = (inv) => {
    const rest = invites.filter(i => i.id !== inv.id);
    setInvites(rest);
    if (!rest.length) dismiss();
  };

  if (!user || !open || invites.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="pending-invite-modal">
      <div className="w-full max-w-md bg-[#0d0d11] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <h2 className="text-white font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            You have {invites.length} invite{invites.length > 1 ? 's' : ''}
          </h2>
          <button onClick={dismiss} data-testid="dismiss-pending-invites" className="p-1 rounded hover:bg-white/5 text-[#8A8A93]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
          {invites.map(inv => (
            <div key={inv.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="w-10 h-10 rounded-lg bg-[#FF4500]/15 text-[#FF4500] flex items-center justify-center shrink-0">
                {inv.kind === 'project' ? <FolderOpen className="w-5 h-5" /> : <Users className="w-5 h-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{inv.name}</p>
                <p className="text-[11px] text-[#8A8A93]">{inv.inviter || 'A teammate'} invited you to this {inv.kind === 'project' ? 'project' : 'workspace'}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button data-testid={`accept-pending-${inv.id}`} disabled={busy === inv.id} onClick={() => accept(inv)}
                  className="px-3 py-1.5 rounded-lg bg-[#FF4500] text-white text-xs font-semibold hover:bg-[#E03C00] disabled:opacity-50 flex items-center gap-1">
                  {busy === inv.id && <Loader2 className="w-3 h-3 animate-spin" />} Accept
                </button>
                <button data-testid={`decline-pending-${inv.id}`} onClick={() => decline(inv)}
                  className="px-3 py-1.5 rounded-lg bg-white/5 text-[#8A8A93] text-xs hover:text-white">Later</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
